import React, { useState, useEffect, useRef } from "react";
import { LuImagePlus, LuX, LuCircleAlert } from "react-icons/lu";

import { t } from "../../i18n";
const formatSize = (bytes) =>
  bytes >= 1048576
    ? t("{n} ميجابايت", { n: (bytes / 1048576).toFixed(1) })
    : t("{n} كيلوبايت", { n: Math.max(1, Math.round(bytes / 1024)) });

// Image upload field: a styled drop zone instead of the browser's English "Choose File" button.
// The native <input type="file"> stays in the page (visually hidden), so keyboard use, screen readers and
// mobile camera/gallery pickers keep working. Invalid files are rejected immediately, in Arabic.
const FileField = ({
  id,
  name,
  label,
  file,
  onChange,
  types = ["image/jpeg", "image/png", "image/jpg"],
  maxBytes = 5 * 1024 * 1024,
  hint = t("JPG أو PNG، بحد أقصى 5 ميجابايت"),
}) => {
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const inputRef = useRef(null);

  // Preview of the chosen image; the object URL is released as soon as it is replaced or removed.
  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const accept = (candidate) => {
    if (!candidate) return;
    if (!types.includes(candidate.type)) {
      setError(t("نوع الصورة غير مدعوم. استخدم صورة بصيغة JPG أو PNG."));
      onChange(null);
    } else if (candidate.size > maxBytes) {
      setError(
        t("حجم الصورة ({size}) يتجاوز الحد المسموح ({max}).", {
          size: formatSize(candidate.size),
          max: formatSize(maxBytes),
        })
      );
      onChange(null);
    } else {
      setError("");
      onChange(candidate);
    }
    if (inputRef.current) inputRef.current.value = ""; // choosing the same file again must still fire
  };

  const remove = () => {
    setError("");
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <span id={`${id}-label`} className="text-slate-700 text-base font-medium text-right block">
        {label}
      </span>

      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-card p-3 shadow-sm">
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t("معاينة الصورة المختارة")}
              className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-slate-200"
            />
          )}
          <div className="min-w-0 flex-1">
            {/* File names are shown left-to-right so extensions like ".png" stay at the end */}
            <p
              dir="ltr"
              className="truncate text-right font-medium text-slate-800"
              title={file.name}
            >
              {file.name}
            </p>
            <p className="text-sm text-slate-600">{formatSize(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={remove}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50"
            aria-label={t("إزالة الصورة")}
          >
            <LuX aria-hidden="true" /> {t("إزالة")}
          </button>
        </div>
      ) : (
        <label
          htmlFor={id}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            accept(e.dataTransfer.files?.[0]);
          }}
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-500 ${
            dragging
              ? "border-brand-500 bg-brand-50"
              : "border-slate-300 bg-slate-50 hover:border-brand-500 hover:bg-brand-50"
          }`}
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-2xl text-brand-700">
            <LuImagePlus aria-hidden="true" />
          </span>
          <span className="font-medium text-brand-700">{t("اضغط لاختيار صورة أو اسحبها هنا")}</span>
          <span className="text-sm text-slate-600">{hint}</span>
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="file"
            accept={types.join(",")}
            onChange={(e) => accept(e.target.files?.[0])}
            aria-describedby={`${id}-hint ${id}-error`}
            className="sr-only"
          />
        </label>
      )}

      <p id={`${id}-hint`} className="sr-only">
        {hint}
      </p>
      <p
        id={`${id}-error`}
        role="alert"
        className="flex min-h-[1.25rem] items-start gap-1 text-sm text-red-600"
      >
        {error && (
          <>
            <LuCircleAlert aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </>
        )}
      </p>
    </div>
  );
};

export default FileField;
